// client/src/components/admin/AdminAwardSettings.js
import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import useAuth from '../../hooks/useAuth'
import ApiAwards from '../../api/ApiAwards'
import PageLayout from '../common/layout/PageLayout'

function AdminAwardSettings() {
  const navigate = useNavigate()
  const { isAdmin, loading: authLoading } = useAuth()
  const [definitions, setDefinitions] = useState([])
  const [config, setConfig] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)

  // Load initial data
  useEffect(() => {
    if (authLoading) return
    if (!isAdmin) {
      navigate('/')
      return
    }

    const loadData = async () => {
      try {
        setLoading(true)
        const [defsResponse, configResponse] = await Promise.all([
          ApiAwards.getAllDefinitions(),
          ApiAwards.getAutoGrantConfig(),
        ])

        if (defsResponse.success) {
          setDefinitions(defsResponse.data?.definitions || [])
        }

        if (configResponse.success) {
          setConfig(
            configResponse.data?.config || {
              globalAutoGrantEnabled: true,
              awards: {},
            },
          )
        }
      } catch (err) {
        console.error('Error loading award settings:', err)
        setError('Failed to load award settings')
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [isAdmin, authLoading, navigate])

  const handleGlobalToggle = useCallback(() => {
    setConfig((prev) => ({
      ...prev,
      globalAutoGrantEnabled: !prev.globalAutoGrantEnabled,
    }))
  }, [])

  const handleAwardToggle = useCallback((awardCode) => {
    setConfig((prev) => {
      const currentAwards = prev.awards || {}
      const currentSetting = currentAwards[awardCode]?.autoGrantEnabled ?? true

      return {
        ...prev,
        awards: {
          ...currentAwards,
          [awardCode]: {
            ...currentAwards[awardCode],
            autoGrantEnabled: !currentSetting,
          },
        },
      }
    })
  }, [])

  const handleSave = useCallback(async () => {
    try {
      setSaving(true)
      setError(null)
      setSuccessMessage(null)

      const response = await ApiAwards.updateAutoGrantConfig(config)

      if (response.success) {
        setSuccessMessage('Award settings saved successfully!')
        setTimeout(() => setSuccessMessage(null), 3000)
      } else {
        setError(response.error || 'Failed to save settings')
      }
    } catch (err) {
      console.error('Error saving award settings:', err)
      setError('Failed to save award settings')
    } finally {
      setSaving(false)
    }
  }, [config])

  const getAwardAutoGrantStatus = useCallback(
    (awardCode) => {
      if (!config) return true
      if (config.globalAutoGrantEnabled === false) return false
      return config.awards?.[awardCode]?.autoGrantEnabled ?? true
    },
    [config],
  )

  if (authLoading || loading) {
    return (
      <PageLayout title="Award Settings">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout title="Award Settings" showBackButton>
      <div className="container-fluid py-4">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-10 col-xl-8">
            {error && (
              <div
                className="alert alert-danger alert-dismissible fade show"
                role="alert"
              >
                {error}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setError(null)}
                  aria-label="Close"
                />
              </div>
            )}

            {successMessage && (
              <div
                className="alert alert-success alert-dismissible fade show"
                role="alert"
              >
                {successMessage}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setSuccessMessage(null)}
                  aria-label="Close"
                />
              </div>
            )}

            <div className="card shadow-sm mb-4">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0">Auto-Grant Configuration</h5>
              </div>
              <div className="card-body">
                <p className="text-muted">
                  Configure which awards are automatically granted when players
                  achieve certain milestones. You can disable auto-granting
                  globally or for individual awards.
                </p>

                <div className="form-check form-switch mb-4">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="globalAutoGrant"
                    checked={config?.globalAutoGrantEnabled ?? true}
                    onChange={handleGlobalToggle}
                    style={{ cursor: 'pointer' }}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="globalAutoGrant"
                    style={{ cursor: 'pointer' }}
                  >
                    <strong>Enable Auto-Granting Globally</strong>
                    <div className="text-muted small">
                      When disabled, no awards will be automatically granted
                      regardless of individual settings
                    </div>
                  </label>
                </div>

                <hr />

                <h6 className="mb-3">Individual Award Settings</h6>

                {definitions.length === 0 ? (
                  <p className="text-muted">No award definitions found.</p>
                ) : (
                  <div className="list-group">
                    {definitions.map((def) => {
                      const isAutoGrantEnabled = getAwardAutoGrantStatus(
                        def.code,
                      )
                      const isDisabledByGlobal =
                        config?.globalAutoGrantEnabled === false

                      return (
                        <div key={def.id} className="list-group-item">
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="flex-grow-1">
                              <div className="d-flex align-items-center mb-1">
                                <h6 className="mb-0">{def.name}</h6>
                                <span className="badge bg-secondary ms-2 small">
                                  {def.category}
                                </span>
                                {!def.is_active && (
                                  <span className="badge bg-warning text-dark ms-2 small">
                                    Inactive
                                  </span>
                                )}
                              </div>
                              <p className="text-muted small mb-1">
                                {def.description}
                              </p>
                              <code className="small text-muted">
                                Code: {def.code}
                              </code>
                            </div>
                            <div className="form-check form-switch ms-3">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`award-${def.code}`}
                                checked={isAutoGrantEnabled}
                                onChange={() => handleAwardToggle(def.code)}
                                disabled={isDisabledByGlobal}
                                style={{
                                  cursor: isDisabledByGlobal
                                    ? 'not-allowed'
                                    : 'pointer',
                                }}
                              />
                              <label
                                className="form-check-label small"
                                htmlFor={`award-${def.code}`}
                                style={{
                                  cursor: isDisabledByGlobal
                                    ? 'not-allowed'
                                    : 'pointer',
                                }}
                              >
                                Auto-grant
                              </label>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
              <div className="card-footer">
                <div className="d-flex justify-content-between align-items-center">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate('/admin')}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Saving...
                      </>
                    ) : (
                      'Save Settings'
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="card shadow-sm">
              <div className="card-header bg-info text-white">
                <h6 className="mb-0">Configuration File</h6>
              </div>
              <div className="card-body">
                <p className="mb-2">
                  Award auto-grant settings are also stored in a configuration
                  file:
                </p>
                <code className="d-block bg-light p-2 rounded">
                  server/config/award-auto-grant.json
                </code>
                <p className="text-muted small mt-2 mb-0">
                  Changes made through this UI are saved to the configuration
                  file. You can also edit the file directly if needed.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  )
}

export default AdminAwardSettings
